import { useEffect, useMemo, useState } from 'react';
import clsx from 'clsx';

import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { Callout } from '@/components/ui/callout';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { StatusCard } from '@/components/ui/card/StatusCard';
import { useTranslation } from '@/hooks/useTranslation';
import type { AdminApiClient, CompanyCount } from '@/features/admin/api/AdminApiClient';

export type DeleteCompanyDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  company: CompanyCount;
  companies: CompanyCount[];
  client: AdminApiClient;
  onSuccess: () => void;
};

export function DeleteCompanyDialog({ open, onOpenChange, company, companies, client, onSuccess }: DeleteCompanyDialogProps) {
  const { t } = useTranslation();
  const targets = useMemo(
    () => companies.filter((c) => c.name !== company.name).map((c) => c.name),
    [companies, company.name]
  );
  const [mode, setMode] = useState<'reassign' | 'cascade'>('reassign');
  const [target, setTarget] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setError(null);
    setSubmitting(false);
    setMode('reassign');
    setTarget(targets[0] ?? '');
  }, [open, targets]);

  // Reassigning needs a target company (none when this is the only company).
  const canConfirm = !(mode === 'reassign' && target.length === 0);

  async function handleConfirm() {
    if (!canConfirm) return;
    setSubmitting(true);
    setError(null);
    if (mode === 'reassign') {
      const reassigned = await client.reassignCompanyUsers(company.name, target);
      if (!reassigned.ok) {
        setSubmitting(false);
        setError(reassigned.message ?? t('administration.errors.generic'));
        return;
      }
      const deleted = await client.deleteCompany(company.name, false);
      setSubmitting(false);
      if (deleted.ok) {
        onSuccess();
        onOpenChange(false);
      } else {
        setError(deleted.message ?? t('administration.errors.generic'));
      }
    } else {
      const deleted = await client.deleteCompany(company.name, true);
      setSubmitting(false);
      if (deleted.ok) {
        onSuccess();
        onOpenChange(false);
      } else {
        setError(deleted.message ?? t('administration.errors.generic'));
      }
    }
  }

  return (
    <ConfirmDialog
      open={open}
      busy={submitting}
      confirmDisabled={!canConfirm}
      tone={mode === 'cascade' ? 'destructive' : 'default'}
      title={t('administration.company.deleteTitle', { name: company.name })}
      description={t('administration.company.deleteBody', { count: company.userCount })}
      confirmLabel={
        mode === 'reassign' ? t('administration.company.reassignConfirm') : t('administration.company.cascadeConfirm')
      }
      cancelLabel={t('common.cancel')}
      onConfirm={() => void handleConfirm()}
      onCancel={() => onOpenChange(false)}
    >
      <div
        className="flex flex-col gap-3"
        role="radiogroup"
        aria-label={t('administration.company.deleteTitle', { name: company.name })}
      >
        <div
          role="radio"
          aria-checked={mode === 'reassign'}
          tabIndex={0}
          onClick={() => setMode('reassign')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setMode('reassign');
            }
          }}
          className={clsx(
            'flex cursor-pointer flex-col gap-1 rounded-lg border p-3 text-left outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
            mode === 'reassign' ? 'border-primary bg-accent' : 'border-input'
          )}
        >
          <span className="text-sm font-semibold text-foreground">{t('administration.company.reassignOption')}</span>
          <span className="text-xs text-muted-foreground">{t('administration.company.reassignDesc')}</span>
          {mode === 'reassign' && targets.length > 0 && (
            <div className="mt-2">
              <Select value={target} onValueChange={setTarget}>
                <SelectTrigger className="w-full" aria-label={t('administration.company.reassignTarget')}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {targets.map((name) => (
                    <SelectItem key={name} value={name}>{name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>

        <div
          role="radio"
          aria-checked={mode === 'cascade'}
          tabIndex={0}
          onClick={() => setMode('cascade')}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setMode('cascade');
            }
          }}
          className={clsx(
            'flex cursor-pointer flex-col gap-1 rounded-lg border p-3 text-left outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
            mode === 'cascade' ? 'border-destructive bg-destructive/5' : 'border-input'
          )}
        >
          <span className="text-sm font-semibold text-destructive">{t('administration.company.cascadeOption')}</span>
          <span className="text-xs text-muted-foreground">{t('administration.company.cascadeDesc')}</span>
        </div>

        <Callout tone="warning">{t('administration.company.warning')}</Callout>

        {error && <StatusCard type="error" message={error} />}
      </div>
    </ConfirmDialog>
  );
}
